import {
  Ard,
  UseCase,
  UslLine,
} from "@amzn/fuse-accounting-central-datastore-client";
import {
  FlareReleaseDomain,
  GetFlareConfigCommandOutput,
} from "@amzn/fuse-ard-service-v2-client";
import {
  FlareNamedMappings,
  FlareNamedMappingPaths,
  SEVEN_SEGMENTS_NAMED_MAPPINGS,
  SevenSegmentsUslAttribute,
  UslLineType,
  parseFlareConfig,
  getFlareValueFromMapping,
} from "@amzn/fuse-flare-common";
import {
  getUslLineTypeFromUsl,
  FLARE_PATH_TO_ARD_MAPPING,
} from "./flare-comparison-helper";
import {
  DiscrepancyStatus,
  FLARE_COMPARISON_USE_CASES,
  USL_LINE_FIELD_NAMES,
} from "./constants";

export interface DiscrepancyResult {
  uslLineId: string;
  field: string;
  ardValue?: string;
  flareValue?: string;
  namedMappingKey?: string;
  status: DiscrepancyStatus;
}

export interface DomainDiscrepancyResults {
  prod: DiscrepancyResult;
  devo: DiscrepancyResult;
  uat: DiscrepancyResult;
}

export interface UslLineDiscrepancies {
  uslLineId: string;
  companyCode?: DomainDiscrepancyResults;
  location?: DomainDiscrepancyResults;
  costCenter?: DomainDiscrepancyResults;
  accountNumber?: DomainDiscrepancyResults;
  productLine?: DomainDiscrepancyResults;
  channel?: DomainDiscrepancyResults;
  project?: DomainDiscrepancyResults;
  hasDiscrepancies: boolean;
  uslLineType?: UslLineType;
}

export interface UseCaseDiscrepancies {
  useCaseName: string;
  altName?: string;
  uslLineDiscrepancies: UslLineDiscrepancies[];
}

export interface ArdFlareComparison {
  ardId: string;
  useCaseDiscrepancies: UseCaseDiscrepancies[];
  timestamp: Date;
}

/**
 * Build input values array for a named mapping lookup
 */
function buildInputValues(
  ard: Ard,
  uslLine: UslLine,
  paths: string[],
): string[] {
  const inputValues: string[] = [];

  for (const path of paths) {
    let value: any;

    // Check if this path corresponds to an ARD-level attribute
    const mapping = FLARE_PATH_TO_ARD_MAPPING[path];

    if (mapping) {
      if (typeof mapping === "function") {
        value = mapping(ard, uslLine);
      } else {
        value = ard[mapping];
      }
    } else {
      // Otherwise, try to get from USL line
      const uslLineKey = path as keyof UslLine;
      value = uslLine[uslLineKey];
    }

    inputValues.push(value?.toString() || "");
  }

  return inputValues;
}

/**
 * Get the Flare value for a specific USL line field
 */
function getFlareValueForField(
  ard: Ard,
  uslLine: UslLine,
  field: SevenSegmentsUslAttribute,
  uslLineType: UslLineType,
  flareMappings: FlareNamedMappings,
  flareMappingPaths: FlareNamedMappingPaths,
): string | undefined {
  // Get the named mapping key for this field and USL line type
  const namedMappingKey = SEVEN_SEGMENTS_NAMED_MAPPINGS[uslLineType]?.[field];
  if (!namedMappingKey) {
    return undefined;
  }

  // Get the mapping data for this named mapping

  const mappingPaths = flareMappingPaths[namedMappingKey];
  if (!mappingPaths || mappingPaths.length === 0) {
    return undefined;
  }

  // Use the first mapping's paths to build input values
  const inputValues = buildInputValues(ard, uslLine, mappingPaths);

  return getFlareValueFromMapping(inputValues, flareMappings, namedMappingKey);
}

/**
 * Generate a unique key for a USL line
 */
function generateUslLineKey(uslLine: UslLine, index: number): string {
  return `${index}_${uslLine.glLineDescriptionSuffix}_${uslLine.debitOrCredit}`;
}

/**
 * Compare a single field
 */
function compareField(
  fieldName: string,
  ardValue: string | undefined,
  flareValue: string | undefined,
): DiscrepancyResult {
  let status: DiscrepancyStatus;

  if (!ardValue && !flareValue) {
    status = "match";
  } else if (!flareValue) {
    status = "missing";
  } else if (ardValue !== flareValue) {
    status = "mismatch";
  } else {
    status = "match";
  }

  return {
    uslLineId: "",
    field: fieldName,
    ardValue,
    flareValue,
    status,
  };
}

/**
 * Mark all USL line fields as missing when line type cannot be determined
 */
function markAllFieldsAsMissing(
  uslLine: UslLine,
  lineId: string,
): UslLineDiscrepancies {
  const discrepancies: UslLineDiscrepancies = {
    uslLineId: lineId,
    hasDiscrepancies: true,
  };

  for (const field of USL_LINE_FIELD_NAMES) {
    const ardValue = uslLine[field]?.toString();

    // Create domain-specific results (all missing)
    const domainResults: DomainDiscrepancyResults = {
      prod: {
        uslLineId: lineId,
        field: field as string,
        ardValue,
        flareValue: undefined,
        status: "missing",
      },
      devo: {
        uslLineId: lineId,
        field: field as string,
        ardValue,
        flareValue: undefined,
        status: "missing",
      },
      uat: {
        uslLineId: lineId,
        field: field as string,
        ardValue,
        flareValue: undefined,
        status: "missing",
      },
    };

    discrepancies[field] = domainResults as any;
  }

  return discrepancies;
}

/**
 * Compare seven segments attributes for a USL line across all domains
 */
function compareSevenSegmentsAttributes(
  ard: Ard,
  uslLine: UslLine,
  uslLineType: UslLineType,
  lineId: string,
  prodMappings: FlareNamedMappings,
  prodMappingPaths: FlareNamedMappingPaths,
  devoMappings: FlareNamedMappings,
  devoMappingPaths: FlareNamedMappingPaths,
  uatMappings: FlareNamedMappings,
  uatMappingPaths: FlareNamedMappingPaths,
  discrepancies: UslLineDiscrepancies,
): void {
  const availableAttributes = SEVEN_SEGMENTS_NAMED_MAPPINGS[uslLineType];

  for (const attrKey of Object.keys(
    availableAttributes,
  ) as Partial<SevenSegmentsUslAttribute>[]) {
    const ardValue = uslLine[attrKey]?.toString();

    // Get Flare values for all three domains
    const prodFlareValue = getFlareValueForField(
      ard,
      uslLine,
      attrKey,
      uslLineType,
      prodMappings,
      prodMappingPaths,
    );

    const devoFlareValue = getFlareValueForField(
      ard,
      uslLine,
      attrKey,
      uslLineType,
      devoMappings,
      devoMappingPaths,
    );

    const uatFlareValue = getFlareValueForField(
      ard,
      uslLine,
      attrKey,
      uslLineType,
      uatMappings,
      uatMappingPaths,
    );

    const currentNamedMappingKey =
      SEVEN_SEGMENTS_NAMED_MAPPINGS[uslLineType]?.[attrKey];

    // Create comparisons for all three domains
    const prodComparison = compareField(
      attrKey as string,
      ardValue,
      prodFlareValue,
    );
    prodComparison.uslLineId = lineId;
    prodComparison.namedMappingKey = currentNamedMappingKey;

    const devoComparison = compareField(
      attrKey as string,
      ardValue,
      devoFlareValue,
    );
    devoComparison.uslLineId = lineId;
    devoComparison.namedMappingKey = currentNamedMappingKey;

    const uatComparison = compareField(
      attrKey as string,
      ardValue,
      uatFlareValue,
    );
    uatComparison.uslLineId = lineId;
    uatComparison.namedMappingKey = currentNamedMappingKey;

    // Create domain-specific results
    const domainResults: DomainDiscrepancyResults = {
      prod: prodComparison,
      devo: devoComparison,
      uat: uatComparison,
    };

    // Check if prod has discrepancies (only prod matters for hasDiscrepancies)
    if (prodComparison.status !== "match") {
      discrepancies.hasDiscrepancies = true;
    }

    discrepancies[attrKey] = domainResults as any;
  }
}

/**
 * Compare a single USL line with flare config mappings across all domains
 */
function compareUslLine(
  ard: Ard,
  uslLine: UslLine,
  lineIndex: number,
  prodMappings: FlareNamedMappings,
  prodMappingPaths: FlareNamedMappingPaths,
  devoMappings: FlareNamedMappings,
  devoMappingPaths: FlareNamedMappingPaths,
  uatMappings: FlareNamedMappings,
  uatMappingPaths: FlareNamedMappingPaths,
): UslLineDiscrepancies {
  const lineId = generateUslLineKey(uslLine, lineIndex);
  const uslLineType = getUslLineTypeFromUsl(uslLine);

  if (!uslLineType) {
    return markAllFieldsAsMissing(uslLine, lineId);
  }

  const discrepancies: UslLineDiscrepancies = {
    uslLineId: lineId,
    hasDiscrepancies: false,
    uslLineType: uslLineType,
  };

  // Compare seven segments attributes across all domains
  compareSevenSegmentsAttributes(
    ard,
    uslLine,
    uslLineType,
    lineId,
    prodMappings,
    prodMappingPaths,
    devoMappings,
    devoMappingPaths,
    uatMappings,
    uatMappingPaths,
    discrepancies,
  );

  return discrepancies;
}

/**
 * Compare an ARD use case with flare config across all domains
 */
function compareUseCase(
  ard: Ard,
  useCase: UseCase,
  prodMappings: FlareNamedMappings,
  prodMappingPaths: FlareNamedMappingPaths,
  devoMappings: FlareNamedMappings,
  devoMappingPaths: FlareNamedMappingPaths,
  uatMappings: FlareNamedMappings,
  uatMappingPaths: FlareNamedMappingPaths,
): UseCaseDiscrepancies {
  const uslLines = useCase.uslLines || [];
  const uslLineDiscrepancies: UslLineDiscrepancies[] = [];

  for (let i = 0; i < uslLines.length; i++) {
    const uslLine = uslLines[i];
    const lineDiscrepancies = compareUslLine(
      ard,
      uslLine,
      i,
      prodMappings,
      prodMappingPaths,
      devoMappings,
      devoMappingPaths,
      uatMappings,
      uatMappingPaths,
    );
    uslLineDiscrepancies.push(lineDiscrepancies);
  }

  return {
    useCaseName: useCase.name || "Unknown",
    altName: useCase.altName,
    uslLineDiscrepancies,
  };
}

// Cache for memoizing parseFlareConfig results
const parseFlareConfigCache = new WeakMap<
  GetFlareConfigCommandOutput,
  ReturnType<typeof parseFlareConfig>
>();

/**
 * Main function to compare ARD with flare configs across all domains
 */
export function compareArdWithFlareConfig(
  ard: Ard,
  flareConfig: GetFlareConfigCommandOutput | null,
): ArdFlareComparison {
  // Parse Flare config once for all three domains (3x faster than separate calls!)
  // Memoize the result to avoid re-parsing the same config
  let parsedResults: ReturnType<typeof parseFlareConfig>;

  if (flareConfig && parseFlareConfigCache.has(flareConfig)) {
    parsedResults = parseFlareConfigCache.get(flareConfig)!;
  } else {
    parsedResults = parseFlareConfig(flareConfig);
    if (flareConfig) {
      parseFlareConfigCache.set(flareConfig, parsedResults);
    }
  }

  // Extract mappings and paths for each domain
  const prodMappings = parsedResults.prod.mappings;
  const prodMappingPaths = parsedResults.prod.mappingPaths;
  const devoMappings = parsedResults.devo.mappings;
  const devoMappingPaths = parsedResults.devo.mappingPaths;
  const uatMappings = parsedResults.uat.mappings;
  const uatMappingPaths = parsedResults.uat.mappingPaths;

  const useCaseDiscrepancies: UseCaseDiscrepancies[] = [];

  // Process each use case in the ARD
  const useCases = ard.usecases || [];
  for (const useCase of useCases) {
    if (useCase.name && !FLARE_COMPARISON_USE_CASES.includes(useCase.name)) {
      continue;
    }

    const useCaseComparison = compareUseCase(
      ard,
      useCase,
      prodMappings,
      prodMappingPaths,
      devoMappings,
      devoMappingPaths,
      uatMappings,
      uatMappingPaths,
    );
    useCaseDiscrepancies.push(useCaseComparison);
  }

  return {
    ardId: ard.id || "",
    useCaseDiscrepancies,
    timestamp: new Date(),
  };
}

/**
 * Interface for discrepancy counts
 */
export interface DiscrepancyCounts {
  mismatches: number;
  missing: number;
  matches: number;
}

/**
 * Count discrepancies from an ArdFlareComparison result (only counts prod domain)
 */
export function countDiscrepancies(
  comparison: ArdFlareComparison | null,
): DiscrepancyCounts {
  const counts: DiscrepancyCounts = {
    mismatches: 0,
    missing: 0,
    matches: 0,
  };

  if (!comparison) {
    return counts;
  }

  // Iterate through all use case discrepancies
  for (const useCaseDisc of comparison.useCaseDiscrepancies) {
    // Iterate through all USL line discrepancies
    for (const uslLineDisc of useCaseDisc.uslLineDiscrepancies) {
      // Check each field in the USL line discrepancy
      for (const fieldName of USL_LINE_FIELD_NAMES) {
        const fieldDisc = uslLineDisc[fieldName];
        if (fieldDisc && typeof fieldDisc === "object" && "prod" in fieldDisc) {
          // Only count prod domain discrepancies
          const domainResults = fieldDisc as DomainDiscrepancyResults;
          const prodResult = domainResults.prod;

          switch (prodResult.status) {
            case "mismatch":
              counts.mismatches++;
              break;
            case "missing":
              counts.missing++;
              break;
            case "match":
              counts.matches++;
              break;
          }
        }
      }
    }
  }

  return counts;
}
